/**
 * A friendly app name from a macOS bundle id — the id's last dot-segment with
 * its first letter capitalized. 'com.apple.mail' -> 'Mail'. Deliberately NO
 * app lookup table: the bundle leaf is friendly-enough, and a table would rot
 * and silently mislabel any app it doesn't know. Returns '' when there is no
 * usable segment (callers fall back to their app-absent copy).
 *
 * One leaf-quality heuristic (P2.5a consent cards): when the leaf is a GENERIC
 * word ('Desktop', 'App', 'Client', …) the vendor segment before it names the
 * app better — 'com.figma.Desktop' -> 'Figma', not 'Desktop'. That is a
 * closed set of generic words, not an app table, so it cannot mislabel.
 *
 * Shipped in P2.4 for the web footer (handsActive.ts); hoisted to shared in
 * P2.5a so the server's chat projection (format-interaction) and the web
 * consent card name apps identically — one implementation, no drift.
 */

/** Leaf segments that describe the PACKAGING, not the app. Lowercase. */
const GENERIC_LEAVES = new Set(['desktop', 'app', 'application', 'client', 'mac', 'macos']);

export function friendlyAppName(bundleId: string): string {
  const segments = bundleId.trim().split('.').filter(Boolean);
  let leaf = segments.pop() ?? '';
  if (!leaf) return '';
  // Generic leaf ('Desktop') ⇒ the vendor segment names the app; keep the
  // generic word only when there's nothing better before it.
  if (GENERIC_LEAVES.has(leaf.toLowerCase()) && segments.length > 1) {
    leaf = segments[segments.length - 1];
  }
  return leaf.charAt(0).toUpperCase() + leaf.slice(1);
}
